import { inject, Injectable } from '@angular/core';
import { LanguageService } from '@catch/services';
import * as countries from 'i18n-iso-countries';
import { BehaviorSubject, map, Observable } from 'rxjs';

// Register locales for EN and DE
import deLocale from 'i18n-iso-countries/langs/de.json';
import enLocale from 'i18n-iso-countries/langs/en.json';

export interface CountryInfo {
  code: string; // Alpha-2 code (e.g., 'DE', 'US')
  name: string; // Localized name
}

@Injectable({
  providedIn: 'root',
})
export class CountryService {
  private languageService = inject(LanguageService);
  private _currentLanguage = new BehaviorSubject<string>('de');

  constructor() {
    // Register required locales
    countries.registerLocale(enLocale);
    countries.registerLocale(deLocale);

    // Subscribe to language changes
    this.languageService.languageChange$.subscribe((lang) => {
      const normalizedLang = this.normalizeLanguageCode(lang ?? 'de');
      this._currentLanguage.next(normalizedLang);
    });

    // Set initial language
    const currentLang = this.languageService.currentLanguage.value;
    this._currentLanguage.next(this.normalizeLanguageCode(currentLang ?? 'de'));
  }

  /**
   * Get localized country name by country code
   */
  getCountryName(countryCode: string, languageCode?: string): string {
    const lang = languageCode || this._currentLanguage.value;
    return countries.getName(countryCode, lang) || countryCode;
  }

  /**
   * Get all countries with localized names
   */
  getAllCountries(languageCode?: string): CountryInfo[] {
    const lang = languageCode || this._currentLanguage.value;
    const countryNames = countries.getNames(lang);

    return Object.entries(countryNames)
      .map(([code, name]) => ({
        code,
        name: name as string,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Get countries as Observable for reactive components
   */
  getAllCountries$(): Observable<CountryInfo[]> {
    return this._currentLanguage.asObservable().pipe(
      map((lang) => this.getAllCountries(lang))
    );
  }

  /**
   * Get country name as Observable for reactive components
   */
  getCountryName$(countryCode: string): Observable<string> {
    return this._currentLanguage.asObservable().pipe(
      map((lang) => this.getCountryName(countryCode, lang))
    );
  }

  /**
   * Search countries by name
   */
  searchCountries(query: string, languageCode?: string): CountryInfo[] {
    if (!query || query.length < 2) {
      return [];
    }

    const allCountries = this.getAllCountries(languageCode);
    const lowerQuery = query.toLowerCase();

    return allCountries.filter((country) =>
      country.name.toLowerCase().includes(lowerQuery) ||
      country.code.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Validate if country code exists
   */
  isValidCountryCode(countryCode: string): boolean {
    return countries.isValid(countryCode);
  }

  /**
   * Convert country name to country code
   */
  getCountryCodeByName(countryName: string, languageCode?: string): string | null {
    const lang = languageCode || this._currentLanguage.value;
    return countries.getAlpha2Code(countryName, lang) || null;
  }

  /**
   * Convert Alpha-3 to Alpha-2 code
   */
  alpha3ToAlpha2(alpha3Code: string): string | null {
    return countries.alpha3ToAlpha2(alpha3Code) || null;
  }

  /**
   * Convert Alpha-2 to Alpha-3 code
   */
  alpha2ToAlpha3(alpha2Code: string): string | null {
    return countries.alpha2ToAlpha3(alpha2Code) || null;
  }

  /**
   * Normalize language code from Angular format to i18n-iso-countries format
   */
  private normalizeLanguageCode(languageCode: string): string {
    // Convert 'de-DE' to 'de', 'en-US' to 'en'
    const lang = languageCode.toLowerCase().split('-')[0];

    // Map to supported languages
    switch (lang) {
      case 'de':
        return 'de';
      case 'en':
        return 'en';
      default:
        return 'de'; // Default fallback
    }
  }
}
